import { CircleAlert as CircleAlertIcon, EllipsisIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Switch } from 'components/ui/switch';
import { useIsMobile } from 'hooks/useIsMobile';
import {
    Children,
    createContext,
    isValidElement,
    ReactElement,
    ReactNode,
    ReactPortal,
    useCallback,
    useContext,
    useState,
    useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from 'utils/cn';
import { useCreateDialogRequest } from './CreateDialogContext';
import { DialogDraft, useDialogDraftKey } from './useCreateDraft';

type FooterProps = {
    /** Receives the element the form's actions are portalled into. */
    slotRef: (element: HTMLElement | null) => void;
    /** Receives the element the number of fields to fix is portalled into, before the actions. */
    errorSlotRef: (element: HTMLElement | null) => void;
};

/**
 * The dialog's footer. Create another is on its left, where the dialog can stay open after a creation; a draft
 * being edited says there when it is deleted instead. The form's actions land on its right: Cancel, its other
 * actions, then the main one, with the number of fields to fix before them. On the sheet the switch and that
 * number each take a row of their own above the actions.
 */
export function CreateDialogFooter({ slotRef, errorSlotRef }: FooterProps) {
    const request = useCreateDialogRequest();
    const draft = useDialogDraftKey();

    return (
        <div className="border-neutral-tint flex min-h-[68px] shrink-0 flex-wrap items-center gap-x-2.5 gap-y-2 border-t px-6 py-3 max-sm:flex-col max-sm:flex-nowrap max-sm:items-stretch max-sm:px-4 max-sm:pt-3 max-sm:pb-4">
            {draft ? (
                <span className="text-meta text-muted-foreground flex-[1_1_12rem]">
                    <DialogDraft draft={draft}>
                        {(detail) =>
                            detail ? (
                                <FormattedMessage
                                    id="draft.expires-on"
                                    values={{ date: <FormattedDate key="date" value={detail.deletesOn} /> }}
                                />
                            ) : null
                        }
                    </DialogDraft>
                </span>
            ) : request?.setCreateAnother ? (
                // Grown from no width, so the actions stay on its row and its words wrap first, as the case
                // form's four actions need.
                <label className="text-body1 flex shrink-0 cursor-pointer items-center gap-2 font-medium select-none max-sm:min-h-11 max-sm:gap-2.5 sm:flex-1">
                    <Switch checked={request.createAnother} onCheckedChange={request.setCreateAnother} />
                    <FormattedMessage id="create-dialog.actions.create-another" />
                </label>
            ) : null}
            {/* Pushed to the end by its own margin: a spacer between the two would cost the row a second gap. The
                two slots draw no box of their own, so what lands in them is laid out as one row, and the number
                is read before the actions, as it is drawn. */}
            <div className="ms-auto flex items-center gap-2.5 max-sm:ms-0 max-sm:flex-wrap">
                <div ref={errorSlotRef} className="contents" />
                <div ref={slotRef} className="contents" />
            </div>
        </div>
    );
}

const ErrorSlotContext = createContext<HTMLElement | null>(null);

/** The footer's slot for the fields to fix, where a modal in the dialog's look has no creation request to hold it. */
export const CreateDialogErrorSlotProvider = ErrorSlotContext.Provider;

/**
 * How many fields are left to fix, before the footer's actions. It is drawn from inside the form, which is where
 * the fields are known, into the footer's slot for it, which comes before the actions' slot.
 */
export function CreateDialogFooterErrors({ count }: { count: number }) {
    const ownSlot = useContext(ErrorSlotContext);
    const errorSlot = useCreateDialogRequest()?.errorSlot ?? ownSlot;

    if (!errorSlot || count === 0) {
        return null;
    }
    return createPortal(
        <span
            role="alert"
            className="text-danger-ink text-meta me-1.5 flex shrink-0 items-center gap-1.5 font-medium whitespace-nowrap max-sm:w-full"
        >
            <CircleAlertIcon className="size-4 shrink-0" />
            <FormattedMessage id="create-dialog.errors.footer" values={{ count }} />
        </span>,
        errorSlot
    );
}

/** Told by display name, as a section is: importing the buttons here would hand every form header their imports. */
function isButton(item: ReactElement, displayName: 'SaveButton' | 'CancelButton'): boolean {
    return typeof item.type !== 'string' && (item.type as { displayName?: string }).displayName === displayName;
}

/**
 * A form's actions by their place in the footer. The main action is the last `SaveButton` the form lists;
 * `null` for a form that lists none, whose actions stay as they are.
 */
function splitActions(actions: ReactNode) {
    const items = Children.toArray(actions).filter(isValidElement);
    const main = items.findLast((item) => isButton(item, 'SaveButton'));

    if (!main) {
        return null;
    }
    return {
        main,
        cancel: items.filter((item) => isButton(item, 'CancelButton')),
        others: items.filter((item) => item !== main && !isButton(item, 'CancelButton')),
    };
}

/**
 * A form lists an action whatever the user may do, and a button the user has no permission for draws nothing.
 * The wrapper its form put around it, left empty, then takes no place among the other actions.
 */
const EMPTY_ACTION_CLASS = '[&>:empty]:hidden';

function FooterActions({ actions }: { actions: ReactNode }) {
    const split = splitActions(actions);

    if (!split) {
        return actions;
    }
    return (
        <>
            {split.cancel}
            <span className={cn('contents', EMPTY_ACTION_CLASS)}>{split.others}</span>
            {split.main}
        </>
    );
}

function createProbe(): HTMLElement {
    return document.createElement('div');
}

/**
 * Whether these actions draw anything to press, which only their output tells. They are rendered a second time,
 * into an element that is never in the document, and that copy is what is read: the menu they are shown in
 * draws them only once it is open.
 */
function useDrawsControl(actions: ReactNode): [probe: ReactPortal, drawsControl: boolean] {
    const [element] = useState(createProbe);
    const subscribe = useCallback(
        (onChange: () => void) => {
            const observer = new MutationObserver(onChange);
            observer.observe(element, { childList: true, subtree: true });
            return () => observer.disconnect();
        },
        [element]
    );
    const drawsControl = useSyncExternalStore(subscribe, () => element.querySelector('button, a, input') !== null);

    return [createPortal(actions, element), drawsControl];
}

type SheetActionsProps = {
    /** The form's actions besides Cancel and the main one. */
    others: ReactNode;
    main: ReactNode;
    /** The id of the menu button's name. */
    moreLabelId?: string;
};

/**
 * On the full-screen sheet the main action takes the row and the form's other actions fold into a menu beside
 * it, which is there only while one of them draws something. Cancel is left out: the header's Close does the same.
 */
export function CreateDialogSheetActions({
    others,
    main,
    moreLabelId = 'create-dialog.actions.more',
}: SheetActionsProps) {
    const intl = useIntl();
    const [othersProbe, hasOthers] = useDrawsControl(others);

    return (
        <>
            {othersProbe}
            {hasOthers ? (
                <Popover>
                    <PopoverTrigger
                        render={
                            <Button
                                variant="outline"
                                size="icon-lg"
                                className="border-border-control size-12 shrink-0"
                                aria-label={intl.formatMessage({ id: moreLabelId })}
                            />
                        }
                    >
                        <EllipsisIcon className="size-[18px]" />
                    </PopoverTrigger>
                    <PopoverContent
                        side="top"
                        align="start"
                        positionerClassName="z-(--z-dropdown)"
                        className={cn('w-auto gap-2 p-2', EMPTY_ACTION_CLASS)}
                    >
                        {others}
                    </PopoverContent>
                </Popover>
            ) : null}
            {main}
        </>
    );
}

/** A form's actions on the sheet, told apart by their place in the footer. */
function SheetActions({ actions }: { actions: ReactNode }) {
    const split = splitActions(actions);

    return split ? <CreateDialogSheetActions others={split.others} main={split.main} /> : actions;
}

type ActionBarProps = {
    actions: ReactNode;
    titleSuffix?: ReactNode;
};

/**
 * What a form's action bar becomes in the create dialog: its actions move to the dialog's footer and its title
 * suffix to the dialog's header. The title itself, the back arrow and the page title are the page's.
 */
export function CreateDialogActionBar({ actions, titleSuffix }: ActionBarProps) {
    const request = useCreateDialogRequest();
    const isMobile = useIsMobile();

    if (!request) {
        return null;
    }
    const { headerSlot, footerSlot } = request;

    return (
        <>
            {footerSlot
                ? createPortal(
                      isMobile ? <SheetActions actions={actions} /> : <FooterActions actions={actions} />,
                      footerSlot
                  )
                : null}
            {titleSuffix && headerSlot ? createPortal(titleSuffix, headerSlot) : null}
        </>
    );
}
